import React from 'react';
import { Card, Typography, Divider, Space, Tag } from 'antd';
import { ReportTemplate, SEPROVINSA_LOGO } from '../types/reportConfig';
import {
  BarChartOutlined,
  FileTextOutlined,
  PictureOutlined,
  BulbOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

interface TemplatePreviewProps {
  template: ReportTemplate;
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ template }) => {
  return (
    <Card
      style={{
        maxWidth: 800,
        margin: '0 auto',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }}
    >
      {/* Header - Diseño Moderno 2025 */}
      <div
        style={{
          background: 'white',
          padding: '32px 24px 24px 24px',
          borderRadius: '8px 8px 0 0',
          marginBottom: 24,
          borderBottom: `3px solid ${template.primaryColor}`,
          position: 'relative',
        }}
      >
        {/* Línea de acento superior */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${template.primaryColor} 0%, ${template.secondaryColor} 100%)`,
            borderRadius: '8px 8px 0 0',
          }}
        />

        {/* Contenedor de logos y título */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 24,
          }}
        >
          {/* Logo Seprovinsa */}
          <div style={{ flex: '0 0 auto' }}>
            <img
              src={SEPROVINSA_LOGO}
              alt="Seprovinsa"
              style={{
                maxWidth: 140,
                maxHeight: 70,
                objectFit: 'contain',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
              }}
            />
          </div>

          {/* Título */}
          <div style={{ flex: 1, textAlign: 'center', padding: '0 16px' }}>
            <Title
              level={3}
              style={{
                color: template.secondaryColor,
                margin: 0,
                fontWeight: 600,
                letterSpacing: '-0.5px',
              }}
            >
              {template.headerText || 'Reporte de Incidencias'}
            </Title>
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: '#64748b',
                fontWeight: 500,
              }}
            >
              {new Date().toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>
          </div>

          {/* Logo del Cliente */}
          <div style={{ flex: '0 0 auto' }}>
            {template.clientLogo ? (
              <img
                src={template.clientLogo}
                alt="Cliente"
                style={{
                  maxWidth: 140,
                  maxHeight: 70,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
                }}
              />
            ) : (
              <div style={{ width: 140 }} />
            )}
          </div>
        </div>
      </div>

      {/* Content Preview */}
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {template.sections.summary && (
          <div>
            <Title level={4} style={{ color: template.primaryColor }}>
              <FileTextOutlined /> Resumen Ejecutivo
            </Title>
            <Text type="secondary">
              Vista general de las incidencias del periodo seleccionado...
            </Text>
          </div>
        )}

        {template.sections.charts && (
          <div>
            <Title level={4} style={{ color: template.primaryColor }}>
              <BarChartOutlined /> Gráficos y Estadísticas
            </Title>
            <div
              style={{
                height: 200,
                background: '#f5f5f5',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `2px dashed ${template.primaryColor}`,
              }}
            >
              <Text type="secondary">Gráficos de incidencias por categoría</Text>
            </div>
          </div>
        )}

        {template.sections.topCategories && (
          <div>
            <Title level={4} style={{ color: template.primaryColor }}>
              Categorías Más Frecuentes
            </Title>
            <Space wrap>
              <Tag color={template.primaryColor}>Mascotas (15)</Tag>
              <Tag color={template.primaryColor}>Iluminación (12)</Tag>
              <Tag color={template.primaryColor}>Vehículos (8)</Tag>
            </Space>
          </div>
        )}

        {template.sections.insights && (
          <div>
            <Title level={4} style={{ color: template.primaryColor }}>
              <BulbOutlined /> Insights y Análisis
            </Title>
            <Card size="small" style={{ background: '#f0f7ff' }}>
              <Text>
                • Zona crítica: Piso 3 con 45 incidencias (30% del total)
                <br />
                • Categoría principal: Deposición de mascotas (35 incidencias)
                <br />• Mejora destacada: Iluminación -50% vs mes anterior
              </Text>
            </Card>
          </div>
        )}

        {template.sections.detailedList && (
          <div>
            <Title level={4} style={{ color: template.primaryColor }}>
              Listado Detallado
            </Title>
            <Text type="secondary">
              Tabla completa con todas las incidencias del periodo...
            </Text>
          </div>
        )}

        {template.sections.photos && (
          <div>
            <Title level={4} style={{ color: template.primaryColor }}>
              <PictureOutlined /> Evidencias Fotográficas
            </Title>
            <Text type="secondary">
              Fotos de las incidencias reportadas...
            </Text>
          </div>
        )}
      </Space>

      <Divider />

      {/* Footer */}
      <div
        style={{
          background: template.secondaryColor,
          padding: '16px',
          borderRadius: '0 0 8px 8px',
          marginTop: 24,
          marginLeft: -24,
          marginRight: -24,
          marginBottom: -24,
        }}
      >
        <Text style={{ color: 'white', fontSize: 12 }}>
          {template.footerText ||
            'Servicios Estratégicos de Protección y Vigilancia Integral S. A.'}
        </Text>
      </div>
    </Card>
  );
};

export default TemplatePreview;
